import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, Suspense, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  SidebarProvider,
  SidebarTrigger,
  Sidebar,
  SidebarContent,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { ChevronDown, Mail, LogOut, ShoppingBag } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AnimatedDashboard,
  AnimatedCalendar,
  AnimatedDollar,
  AnimatedInbox,
  AnimatedClipboard,
  AnimatedSettings,
  AnimatedUsers,
  AnimatedOrgSettings,
  AnimatedAllOrgs,
  AnimatedLogout,
  AnimatedShoppingBag,
} from "@/components/AnimatedIcons";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { canEditOrgSettings, type OrganizationRole } from "@tickethall/core";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { OrganizationSwitcher } from "@/components/OrganizationSwitcher";
import { PanelBreadcrumbs } from "@/components/PanelBreadcrumbs";
import { ProducerCommandPalette } from "@/components/producer/ProducerCommandPalette";
import { ProducerRouteSkeleton } from "@/components/producer/ProducerPageSkeleton";
import { EventFocusSlotProvider, useEventFocusSlot } from "@/contexts/EventFocusSlotContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { panelAccentDot } from "@/lib/panel-ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getProducerDashboardStats, getProducerEvents, getAllProducerEvents } from "@/lib/api-producer";
import { getCashFlowSummary } from "@/lib/api-financial";

interface ProducerSidebarProps {
  onNavigate?: (url: string) => void;
}

function NavItemHover({ children, className }: { children: (hovered: boolean) => ReactNode; className?: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      className={className}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {children(hovered)}
    </motion.div>
  );
}

function ProducerSidebar({ onNavigate }: ProducerSidebarProps) {
  const reduceMotion = useReducedMotion();
  const { t } = useTranslation(["producerCommon", "common"]);
  const { state, isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const collapsed = state === "collapsed" && !isMobile;
  const { signOut, profile, allRoles, role, switchRole, user } = useAuth();
  const { activeContext, organizations } = useOrganization();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setSlot } = useEventFocusSlot();

  const activeOrgRole = activeContext.organizationId && organizations
    ? (organizations.find((o) => o.org_id === activeContext.organizationId)?.org_role as OrganizationRole | undefined)
    : undefined;
  const canEditActiveOrg = activeOrgRole ? canEditOrgSettings(activeOrgRole) : false;
  const isOrgContext = activeContext.type === "organization" && !!activeContext.organizationId;

  // Modo foco: exclusivamente dentro do painel de um evento individual OU nas telas de previsão/histórico financeiro.
  // Na visão geral / conciliação e no fluxo de caixa, a navegação principal da plataforma permanece sempre ativa.
  const currentTab = new URLSearchParams(location.search).get("tab") || "conciliacao";
  const isEventPanelFocus = /^\/(?:producer|produtor)\/(?:events|eventos)\/[^/]+\/(?:panel|painel)/.test(location.pathname);
  const isFinancialRoute = location.pathname.startsWith("/produtor/financeiro") || location.pathname.startsWith("/producer/financial");
  const isWithdrawalView = isFinancialRoute && ["previsao", "historico"].includes(currentTab);
  const eventFocus = isEventPanelFocus || isWithdrawalView;
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isFinancialOpen, setIsFinancialOpen] = useState(isFinancialRoute);

  const isOrgMembersRoute = Boolean(
    activeContext.organizationId &&
    location.pathname.startsWith(`/produtor/organizacoes/${activeContext.organizationId}/membros`)
  );
  const [isTeamOpen, setIsTeamOpen] = useState(true);

  // Mantém aberto se navegar diretamente para membros ou time
  useEffect(() => {
    if (isOrgMembersRoute) {
      setIsTeamOpen(true);
    }
  }, [isOrgMembersRoute]);

  const currentOrgTab = new URLSearchParams(location.search).get("tab") || "membros";

  const teamSubItems = useMemo(() => {
    if (!activeContext.organizationId) return [];
    return [
      {
        title: t("layout.org.subItems.members", { defaultValue: "Membros do Time" }),
        tab: "membros",
        url: `/produtor/organizacoes/${activeContext.organizationId}/membros?tab=membros`,
      },
      {
        title: t("layout.org.subItems.invites", { defaultValue: "Convites Pendentes" }),
        tab: "convites",
        url: `/produtor/organizacoes/${activeContext.organizationId}/membros?tab=convites`,
      },
      {
        title: t("layout.org.subItems.roles", { defaultValue: "Cargos & Permissões" }),
        tab: "cargos",
        url: `/produtor/organizacoes/${activeContext.organizationId}/membros?tab=cargos`,
      },
    ];
  }, [activeContext.organizationId, t]);

  // Mantém aberto se navegar diretamente para o financeiro
  useEffect(() => {
    if (isFinancialRoute) {
      setIsFinancialOpen(true);
    }
  }, [isFinancialRoute]);

  const handleLinkClick = (url: string) => {
    if (isMobile) {
      setOpenMobile(false);
    }
    onNavigate?.(url);
  };

  const prefetchRoute = (url: string) => {
    try {
      if (url.includes("dashboard")) {
        if (isOrgContext) {
          queryClient.prefetchQuery({
            queryKey: ["organization-dashboard", activeContext.organizationId, null],
            queryFn: () => getProducerDashboardStats(user!.id, activeContext.organizationId!, undefined),
            staleTime: 5 * 60_000,
          });
        } else if (user?.id) {
          queryClient.prefetchQuery({
            queryKey: ["producer-dashboard", user.id, null],
            queryFn: () => getProducerDashboardStats(user.id, undefined, undefined),
            staleTime: 5 * 60_000,
          });
        }
      } else if (url.includes("eventos") || url.includes("events")) {
        if (user?.id) {
          const orgsKey = (organizations || []).map((o) => o.org_id).sort().join(",");
          queryClient.prefetchQuery({
            queryKey: ["all-producer-events", user.id, orgsKey],
            queryFn: () => getAllProducerEvents(user.id, organizations),
            staleTime: 60_000,
          });
        }
      } else if (url.includes("financeiro") || url.includes("financial")) {
        if (user?.id) {
          queryClient.prefetchQuery({
            queryKey: ["cash-flow", user.id],
            queryFn: () => getCashFlowSummary(user.id),
            staleTime: 30_000,
          });
        }
      }
    } catch {
      // Prefetch silencioso, sem quebrar UX
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()
    : "P";

  const navWorkspaceItems = useMemo(() => [
    { title: t("layout.nav.dashboard", { defaultValue: "Dashboard" }), url: "/produtor/dashboard", icon: AnimatedDashboard },
    { title: t("layout.nav.events", { defaultValue: "Meus Eventos" }), url: "/produtor/eventos", icon: AnimatedCalendar },
    {
      title: t("layout.nav.financial.label", { defaultValue: "Financeiro" }),
      url: "/produtor/financeiro",
      icon: AnimatedDollar,
      subItems: [
        { title: t("layout.nav.financial.subItems.dashboard", { defaultValue: "Visão Geral" }), url: "/produtor/financeiro?tab=conciliacao" },
        { title: t("layout.nav.financial.subItems.withdrawals", { defaultValue: "Saques" }), url: "/produtor/financeiro?tab=saques" },
      ],
    },
    { title: t("layout.nav.inbox", { defaultValue: "Inbox & SAC" }), url: "/produtor/inbox", icon: AnimatedInbox },
    { title: t("layout.nav.interestLists", { defaultValue: "Listas de Interesse" }), url: "/produtor/lista-de-interesse", icon: AnimatedClipboard },
    { title: t("common:nationalFiscal.title"), url: "/produtor/notas-fiscais", icon: AnimatedClipboard },
    { title: t("layout.nav.settings", { defaultValue: "Configurações" }), url: "/produtor/configuracoes", icon: AnimatedSettings },
  ], [t]);

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="flex h-full min-h-0 flex-col gap-0 overflow-hidden bg-sidebar">
        {/* Brand */}
        <div className={cn(
          "flex shrink-0 items-center",
          collapsed ? "justify-center px-2 py-4" : "gap-2.5 px-4 py-4",
          isMobile && "border-b border-border/60 px-4 py-5",
        )}>
          <Link
            to="/produtor/dashboard"
            className="flex min-w-0 items-center gap-2.5"
            onClick={() => handleLinkClick("/produtor/dashboard")}
          >
            {collapsed ? (
              <TicketHallLogo variant="symbol" size="sm" />
            ) : (
              <>
                <TicketHallLogo size={isMobile ? "md" : "lg"} />
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {t("layout.brand.menuBadge", { defaultValue: "Produtor" })}
                </span>
              </>
            )}
          </Link>
        </div>

        {/* Modo foco: slot onde o painel do evento porta sua navegação */}
        {eventFocus && (
          <div ref={setSlot} className="min-h-0 flex-1 overflow-y-auto px-2" />
        )}

        {/* Navegação Principal */}
        {!eventFocus && (
          <nav className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2">
            {/* Workspace Group */}
            <div className="mb-4">
              {!collapsed && (
                <p className="px-2.5 pb-1.5 text-[11px] font-medium text-muted-foreground">
                  {t("layout.nav.groupLabel", { defaultValue: "Workspace" })}
                </p>
              )}
              <ul className="space-y-0.5">
                {navWorkspaceItems.map((item) => {
                  const hasSubItems = Boolean(item.subItems && item.subItems.length > 0);

                  if (hasSubItems) {
                    return (
                      <li key={item.url} className="space-y-0.5">
                        <NavItemHover>
                          {(hovered) => (
                            <div className="flex items-center">
                              <button
                                aria-expanded={!collapsed && isFinancialOpen}
                                aria-controls={!collapsed && isFinancialOpen ? "producer-financial-submenu" : undefined}
                                type="button"
                                onClick={() => {
                                  if (collapsed) {
                                    handleLinkClick(item.url);
                                    navigate(item.url);
                                  } else {
                                    setIsFinancialOpen((prev) => !prev);
                                  }
                                }}
                                onMouseEnter={() => prefetchRoute(item.url)}
                                onFocus={() => prefetchRoute(item.url)}
                                title={collapsed ? item.title : undefined}
                                className={cn(
                                  "group flex w-full items-center rounded-xl text-sm transition-colors duration-150 text-left",
                                  collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                                  isMobile && "min-h-11 py-2.5 text-[15px]",
                                  isFinancialRoute
                                    ? "bg-secondary/70 text-foreground font-medium hover:bg-secondary"
                                    : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
                                )}
                              >
                                <item.icon isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                                {!collapsed && (
                                  <>
                                    <span className="min-w-0 flex-1 truncate">{item.title}</span>
                                    <span className="p-1 rounded-md text-muted-foreground group-hover:text-foreground transition-transform">
                                      <ChevronDown
                                        className={cn(
                                          "h-4 w-4 transition-transform duration-200 motion-reduce:transition-none",
                                          isFinancialOpen && "rotate-180"
                                        )}
                                      />
                                    </span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </NavItemHover>

                        {/* Submenu Apple Minimalist */}
                        <AnimatePresence initial={false}>
                        {!collapsed && isFinancialOpen && (
                          <motion.ul
                            key="financial-submenu"
                            id="producer-financial-submenu"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className={cn(
                            "flex flex-col overflow-hidden border-l border-border/50 space-y-0.5 mt-1",
                            isMobile ? "ml-5 pl-2.5" : "ml-6 pl-2.5"
                          )}>
                            {item.subItems!.map((subItem) => {
                              const tabParam = new URLSearchParams(subItem.url.split("?")[1] || "").get("tab");
                              const isSubActive = isFinancialRoute && (
                                tabParam === "saques"
                                  ? ["saques", "previsao", "historico", "bancos"].includes(currentTab)
                                  : currentTab === "conciliacao"
                              );

                              return (
                                <li key={subItem.url}>
                                  <Link
                                    to={subItem.url}
                                    onClick={() => handleLinkClick(subItem.url)}
                                    className={cn(
                                      "group flex items-center justify-between rounded-lg transition-colors duration-150",
                                      isMobile ? "min-h-[44px] px-3 py-2 text-[14px]" : "px-2.5 py-1.5 text-[13px]",
                                      isSubActive
                                        ? "bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                                        : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                                    )}
                                  >
                                    <span className="truncate">{subItem.title}</span>
                                    <span
                                      className={cn(panelAccentDot, isSubActive ? "opacity-100" : "opacity-0")}
                                      aria-hidden
                                    />
                                  </Link>
                                </li>
                              );
                            })}
                          </motion.ul>
                        )}
                        </AnimatePresence>
                      </li>
                    );
                  }

                  return (
                    <li key={item.url}>
                      <NavItemHover>
                        {(hovered) => (
                          <NavLink
                            to={item.url}
                            end={item.url === "/produtor/dashboard" || item.url === "/producer/dashboard"}
                            onClick={() => handleLinkClick(item.url)}
                            onMouseEnter={() => prefetchRoute(item.url)}
                            onFocus={() => prefetchRoute(item.url)}
                            title={collapsed ? item.title : undefined}
                            className={cn(
                              "group flex w-full items-center rounded-xl text-sm text-muted-foreground transition-colors duration-150",
                              collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                              isMobile && "min-h-11 py-2.5 text-[15px]",
                              "hover:bg-secondary/70 hover:text-foreground",
                            )}
                            activeClassName="bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                          >
                            <item.icon isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                            {!collapsed && (
                              <>
                                <span className="min-w-0 flex-1 truncate">{item.title}</span>
                                <span
                                  className={cn(panelAccentDot, "opacity-0 group-aria-[current=page]:opacity-100")}
                                  aria-hidden
                                />
                              </>
                            )}
                          </NavLink>
                        )}
                      </NavItemHover>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Organization Group */}
            {isOrgContext ? (
              <div className="mb-4">
                {!collapsed && (
                  <div className="flex items-center justify-between px-2.5 pb-1.5">
                    <p className="text-[11px] font-medium text-muted-foreground truncate flex-1">
                      {activeContext.organizationName}
                    </p>
                    {activeOrgRole && (
                      <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground shrink-0">
                        {activeOrgRole === "owner" ? t("layout.org.roles.owner", { defaultValue: "Proprietário" }) : activeOrgRole}
                      </span>
                    )}
                  </div>
                )}
                <ul className="space-y-0.5">
                  <li className="space-y-0.5">
                    <NavItemHover>
                      {(hovered) => (
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              if (collapsed) {
                                handleLinkClick(`/produtor/organizacoes/${activeContext.organizationId}/membros?tab=membros`);
                                navigate(`/produtor/organizacoes/${activeContext.organizationId}/membros?tab=membros`);
                              } else if (!isOrgMembersRoute) {
                                setIsTeamOpen(true);
                                handleLinkClick(`/produtor/organizacoes/${activeContext.organizationId}/membros?tab=membros`);
                                navigate(`/produtor/organizacoes/${activeContext.organizationId}/membros?tab=membros`);
                              } else {
                                setIsTeamOpen((prev) => !prev);
                              }
                            }}
                            title={collapsed ? t("layout.org.manageTeam", { defaultValue: "Gerenciar time" }) : undefined}
                            className={cn(
                              "group flex w-full items-center rounded-xl text-sm transition-colors duration-150 text-left",
                              collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                              isMobile && "min-h-11 py-2.5 text-[15px]",
                              isOrgMembersRoute
                                ? "bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                                : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground",
                            )}
                          >
                            <AnimatedUsers isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                            {!collapsed && (
                              <>
                                <span className="min-w-0 flex-1 truncate">{t("layout.org.manageTeam", { defaultValue: "Gerenciar time" })}</span>
                                <span className="p-1 rounded-md text-muted-foreground group-hover:text-foreground transition-transform">
                                  <ChevronDown
                                    className={cn(
                                      "h-4 w-4 transition-transform duration-200 motion-reduce:transition-none",
                                      isTeamOpen && "rotate-180"
                                    )}
                                  />
                                </span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </NavItemHover>

                    {/* Submenu de Gerenciar Time */}
                    {!collapsed && isTeamOpen && (
                      <ul className={cn(
                        "flex flex-col border-l border-border/50 space-y-0.5 mt-1",
                        isMobile ? "ml-5 pl-2.5" : "ml-6 pl-2.5"
                      )}>
                        {teamSubItems.map((subItem) => {
                          const isSubActive = isOrgMembersRoute && currentOrgTab === subItem.tab;

                          return (
                            <li key={subItem.url}>
                              <Link
                                to={subItem.url}
                                onClick={() => handleLinkClick(subItem.url)}
                                className={cn(
                                  "group flex items-center justify-between rounded-lg transition-colors duration-150",
                                  isMobile ? "min-h-[44px] px-3 py-2 text-[14px]" : "px-2.5 py-1.5 text-[13px]",
                                  isSubActive
                                    ? "bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                                    : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                                )}
                              >
                                <span className="truncate">{subItem.title}</span>
                                <span
                                  className={cn(panelAccentDot, isSubActive ? "opacity-100" : "opacity-0")}
                                  aria-hidden
                                />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                  {canEditActiveOrg && (
                    <li>
                      <NavItemHover>
                        {(hovered) => (
                          <NavLink
                            to={`/produtor/organizacoes/${activeContext.organizationId}/configuracoes`}
                            onClick={() => handleLinkClick(`/produtor/organizacoes/${activeContext.organizationId}/configuracoes`)}
                            title={collapsed ? t("layout.org.orgSettings", { defaultValue: "Configurações da org" }) : undefined}
                            className={cn(
                              "group flex w-full items-center rounded-xl text-sm text-muted-foreground transition-colors duration-150",
                              collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                              isMobile && "min-h-11 py-2.5 text-[15px]",
                              "hover:bg-secondary/70 hover:text-foreground",
                            )}
                            activeClassName="bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                          >
                            <AnimatedOrgSettings isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                            {!collapsed && (
                              <>
                                <span className="min-w-0 flex-1 truncate">{t("layout.org.orgSettings", { defaultValue: "Configurações da org" })}</span>
                                <span className={cn(panelAccentDot, "opacity-0 group-aria-[current=page]:opacity-100")} aria-hidden />
                              </>
                            )}
                          </NavLink>
                        )}
                      </NavItemHover>
                    </li>
                  )}
                  <li className="pt-1.5 border-t border-border/40 mt-1.5">
                    <NavItemHover>
                      {(hovered) => (
                        <NavLink
                          to="/produtor/organizacoes"
                          end
                          onClick={() => handleLinkClick("/produtor/organizacoes")}
                          title={collapsed ? t("layout.org.allOrganizations", { defaultValue: "Todas organizações" }) : undefined}
                          className={cn(
                            "group flex w-full items-center rounded-xl text-sm text-muted-foreground transition-colors duration-150",
                            collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                            isMobile && "min-h-11 py-2.5 text-[15px]",
                            "hover:bg-secondary/70 hover:text-foreground",
                          )}
                          activeClassName="bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                        >
                          <AnimatedAllOrgs isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                          {!collapsed && (
                            <>
                              <span className="min-w-0 flex-1 truncate">{t("layout.org.allOrganizations", { defaultValue: "Todas organizações" })}</span>
                              <span className={cn(panelAccentDot, "opacity-0 group-aria-[current=page]:opacity-100")} aria-hidden />
                            </>
                          )}
                        </NavLink>
                      )}
                    </NavItemHover>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="mb-4">
                {!collapsed && (
                  <p className="px-2.5 pb-1.5 text-[11px] font-medium text-muted-foreground truncate">
                    {t("layout.nav.organizationsGroup", { defaultValue: "Organizações" })}
                  </p>
                )}
                <ul className="space-y-0.5">
                  <li>
                    <NavItemHover>
                      {(hovered) => (
                        <NavLink
                          to="/produtor/organizacoes"
                          end
                          onClick={() => handleLinkClick("/produtor/organizacoes")}
                          title={collapsed ? t("layout.org.allOrganizations", { defaultValue: "Todas organizações" }) : undefined}
                          className={cn(
                            "group flex w-full items-center rounded-xl text-sm text-muted-foreground transition-colors duration-150",
                            collapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-2.5 py-2",
                            isMobile && "min-h-11 py-2.5 text-[15px]",
                            "hover:bg-secondary/70 hover:text-foreground",
                          )}
                          activeClassName="bg-secondary text-foreground font-medium hover:bg-secondary hover:text-foreground"
                        >
                          <AnimatedAllOrgs isHovered={hovered} className={cn("shrink-0", isMobile ? "h-5 w-5" : "h-4 w-4")} />
                          {!collapsed && (
                            <>
                              <span className="min-w-0 flex-1 truncate">{t("layout.org.allOrganizations", { defaultValue: "Todas organizações" })}</span>
                              <span className={cn(panelAccentDot, "opacity-0 group-aria-[current=page]:opacity-100")} aria-hidden />
                            </>
                          )}
                        </NavLink>
                      )}
                    </NavItemHover>
                  </li>
                </ul>
              </div>
            )}
          </nav>
        )}

        {/* Footer & Perfil Apple Minimalist */}
        <div className={cn(
          "mt-auto shrink-0 border-t border-border/60",
          collapsed ? "p-2" : "p-3",
          isMobile && "pb-6",
        )}>
          {!collapsed && (
            <div className="mb-2 px-2.5 pt-2 pb-1">
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 shrink-0">
                  {profile?.avatar_url && (
                    <AvatarImage
                      src={profile.avatar_url}
                      alt={profile.full_name || t("layout.profile.fallbackName", { defaultValue: "Produtor" })}
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback className="bg-secondary text-foreground text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {profile?.full_name || t("layout.profile.fallbackName", { defaultValue: "Produtor" })}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("layout.profile.roleLabel", { defaultValue: "Produtor" })}
                  </p>
                </div>
              </div>

              {allRoles.includes("buyer") && role !== "buyer" && (
                <NavItemHover>
                  {(hovered) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                        switchRole("buyer");
                        navigate("/");
                      }}
                      className="mt-2 min-h-11 w-full justify-start gap-2 rounded-xl px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-2"
                    >
                      <AnimatedShoppingBag isHovered={hovered} className="h-3.5 w-3.5" />
                      {t("layout.profile.switchToBuyer", { defaultValue: "Alternar para Comprador" })}
                    </Button>
                  )}
                </NavItemHover>
              )}
            </div>
          )}

          <NavItemHover>
            {(hovered) => (
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "w-full gap-2.5 text-muted-foreground hover:bg-secondary hover:text-foreground",
                  collapsed ? "h-10 justify-center px-0" : "h-10 justify-start px-2.5",
                  isMobile && "h-11",
                )}
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                  setShowLogoutDialog(true);
                }}
              >
                <AnimatedLogout isHovered={hovered} className="h-4 w-4 shrink-0" />
                {!collapsed && t("layout.actions.signOut", { defaultValue: "Sair do painel" })}
              </Button>
            )}
          </NavItemHover>
        </div>
      </SidebarContent>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="gap-0 rounded-[28px] p-6 sm:max-w-[400px] sm:p-7">
          <DialogHeader className="space-y-2 pr-6 text-left">
            <DialogTitle className="font-display text-2xl font-semibold leading-tight tracking-tight">
              {t("layout.logoutDialog.title")}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              {t(allRoles.includes("buyer") && role !== "buyer" ? "layout.logoutDialog.description" : "layout.logoutDialog.signOut.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 pt-6">
            {allRoles.includes("buyer") && role !== "buyer" && (
              <div className="pb-3">
                <Button
                  type="button"
                  onClick={() => {
                    setShowLogoutDialog(false);
                    switchRole("buyer");
                    navigate("/");
                  }}
                  className="min-h-12 w-full gap-2 rounded-full px-5 text-sm font-medium"
                >
                  <ShoppingBag aria-hidden="true" className="pointer-events-none h-4 w-4" />
                  {t("layout.logoutDialog.switchToBuyer.title")}
                </Button>
                <p className="mt-2.5 text-center text-xs leading-relaxed text-muted-foreground">
                  {t("layout.logoutDialog.switchToBuyer.description")}
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setShowLogoutDialog(false);
                signOut();
              }}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-secondary px-5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <LogOut aria-hidden="true" className="pointer-events-none h-4 w-4 text-muted-foreground" />
              {t("layout.logoutDialog.signOut.title")}
            </button>
          </div>

          <DialogFooter className="mt-5 border-t border-border/50 pt-3 sm:justify-center">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowLogoutDialog(false)}
              className="min-h-11 w-full rounded-full text-sm font-normal text-muted-foreground hover:text-foreground"
            >
              {t("common:cancel", { defaultValue: "Cancelar" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}

function PendingInvitesBanner() {
  const { t } = useTranslation("producerCommon");
  const { organizations } = useOrganization();
  const location = useLocation();
  const pendingInvites = organizations.filter((o) => o.member_status === "pending");

  if (pendingInvites.length === 0 || location.pathname.startsWith("/produtor/organizacoes") || location.pathname.startsWith("/producer/organizacoes")) {
    return null;
  }

  return (
    <Link
      to="/produtor/organizacoes"
      className="flex items-center gap-2 px-4 py-2 text-xs font-medium bg-secondary/80 text-foreground hover:bg-secondary transition-colors border-b border-border/60 shrink-0"
    >
      <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      {t("layout.pendingInvites.message", { count: pendingInvites.length, orgName: pendingInvites[0].org_name })}
      <span className="underline ml-1">{t("layout.pendingInvites.cta", { defaultValue: "Ver convites" })}</span>
    </Link>
  );
}

export default function ProducerLayout() {
  const location = useLocation();
  const [navigatingTo, setNavigatingTo] = useState<string | null>(null);

  useEffect(() => {
    const savedDashTheme = localStorage.getItem("theme-dashboard");
    if (!savedDashTheme) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme-dashboard", "light");
    }
  }, []);

  // Quando o caminho real de navegação é alcançado, limpa o estado de transição otimista
  useEffect(() => {
    setNavigatingTo(null);
  }, [location.pathname, location.search]);

  const handleNavigate = (url: string) => {
    const currentFull = location.pathname + (location.search || "");
    if (url !== currentFull) {
      setNavigatingTo(url);
    }
  };

  const isProducerEventFormRoute = /^\/(producer|produtor)\/(events|eventos)\/(new|novo|[^/]+\/(edit|editar))$/.test(location.pathname);
  const isEventPanelFocus = /^\/(producer|produtor)\/(events|eventos)\/[^/]+\/(panel|painel)/.test(location.pathname);
  const currentTab = new URLSearchParams(location.search).get("tab") || "conciliacao";
  const isFinancialRoute = location.pathname.startsWith("/produtor/financeiro") || location.pathname.startsWith("/producer/financial");
  const isWithdrawalView = isFinancialRoute && ["previsao", "historico"].includes(currentTab);
  const eventFocus = isEventPanelFocus || isWithdrawalView;

  return (
    <SidebarProvider
      open={true}
      className="h-screen overflow-hidden bg-background"
      style={eventFocus ? ({ "--sidebar-width": "20rem" } as CSSProperties) : undefined}
    >
      <EventFocusSlotProvider>
        <div className="h-full flex w-full bg-background overflow-hidden">
          <ProducerSidebar onNavigate={handleNavigate} />
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <PendingInvitesBanner />

            {/* Top bar Apple Minimalist */}
            <header className="relative flex flex-col shrink-0 bg-background/80 backdrop-blur-lg border-b border-border/40">
              {/* Barra de progresso ultra-fina quando estiver navegando */}
              {navigatingTo && (
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-secondary overflow-hidden z-50">
                  <div className="h-full bg-primary/80 animate-pulse w-full" />
                </div>
              )}

              <div className="h-16 flex items-center justify-between px-4 md:px-6 gap-2 sm:gap-4">
                <div className="flex min-w-0 items-center gap-3 shrink-0">
                  <SidebarTrigger className="h-9 w-9 rounded-lg border border-border bg-card hover:bg-muted shrink-0 lg:hidden" />
                  <Separator orientation="vertical" className="h-6 shrink-0 hidden sm:block lg:hidden" />
                  <PanelBreadcrumbs className="hidden sm:block" />
                </div>
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 ml-auto">
                  <ProducerCommandPalette />
                  <OrganizationSwitcher />
                  <NotificationBell />
                </div>
              </div>
              {/* No mobile o breadcrumb desce pra própria linha */}
              <PanelBreadcrumbs className="sm:hidden px-4 pb-2 -mt-1 overflow-x-auto" />
            </header>

            {/* Content Area com Skeleton Imediato */}
            <main
              data-scroll-container
              className={cn(
                "flex-1 min-h-0 px-3 py-4 sm:px-4 md:px-6 md:py-6 lg:px-7 [scrollbar-gutter:stable]",
                isProducerEventFormRoute ? "lg:overflow-hidden overflow-y-auto overscroll-contain" : "overflow-y-auto overscroll-contain",
              )}
            >
              <Suspense
                fallback={
                  <div className="mx-auto w-full max-w-[1380px]">
                    <ProducerRouteSkeleton path={location.pathname} />
                  </div>
                }
              >
                {isProducerEventFormRoute ? (
                  <Outlet />
                ) : (
                  <div className="mx-auto w-full max-w-[1380px]">
                    <Outlet />
                  </div>
                )}
              </Suspense>
            </main>
          </div>
        </div>
      </EventFocusSlotProvider>
    </SidebarProvider>
  );
}
